import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { band, linear, niceMax, ticks } from '../chart-scales';
import type { CandidatesModel } from '../analytics-charts.model';
import { useElementWidth } from '../use-element-width';
import { Bar } from './svg-parts';

// d) Candidatos según cuántos cursos les faltan (0 = cumplen). Solo conteos:
// no se suprime (PM). Los nombres están en la pestaña Candidatos, no aquí.

export function CandidatesChart({ model }: { model: CandidatesModel }) {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const { ref, width: W } = useElementWidth<HTMLDivElement>();
  const ink = theme.palette.text.secondary;
  const grid = theme.palette.divider;
  const m = { l: 36, r: 8, t: 22, b: 44 };
  const H = 228;
  const rows = model.rows;
  const bx = band(rows.length, m.l, Math.min(W - m.r, m.l + rows.length * 120), 0.35);
  const right = bx.x(rows.length - 1) + bx.width + 8;
  const { max, step } = niceMax(Math.max(1, ...rows.map((r) => r.total)));
  const y = linear(0, max, H - m.b, m.t);
  const label = (k: number) => (k === 0 ? t('reports.analytics.candidates.zero') : String(k));

  return (
    <div ref={ref}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t('reports.analytics.candidates.title')}
      >
        {ticks(max, step).map((v) => (
          <g key={v}>
            <line x1={m.l} x2={right} y1={y(v)} y2={y(v)} stroke={grid} />
            <text x={m.l - 6} y={y(v) + 4} fontSize={11} textAnchor="end" fill={ink}>
              {v}
            </text>
          </g>
        ))}
        {rows.map((r, i) => (
          <g key={r.missing}>
            <Bar
              x={bx.x(i)}
              y={y(r.total)}
              w={bx.width}
              base={H - m.b}
              fill={theme.palette.primary.main}
            >
              <title>{`${label(r.missing)}: ${t('reports.analytics.candidates.count', { count: r.total })}`}</title>
            </Bar>
            <text
              x={bx.center(i)}
              y={y(r.total) - 6}
              fontSize={12}
              fontWeight={600}
              textAnchor="middle"
              fill={theme.palette.text.primary}
            >
              {r.total}
            </text>
            <text x={bx.center(i)} y={H - m.b + 16} fontSize={11.5} textAnchor="middle" fill={ink}>
              {label(r.missing)}
            </text>
          </g>
        ))}
        <text
          x={(bx.x(0) + right - 8) / 2}
          y={H - 4}
          fontSize={11.5}
          textAnchor="middle"
          fill={ink}
        >
          {t('reports.analytics.candidates.x')}
        </text>
      </svg>
    </div>
  );
}
